/**
 * Pin a chart, table or number to a dashboard: name it, then pick one of the
 * dashboards that take widgets or name a new one. Built from the UI kit.
 */

import React from 'react';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import BarChartRounded from '@mui/icons-material/BarChartRounded';
import { EmptyState, Modal, SearchField, TilePicker } from '../ui-kit';
import { formatRelativeTime } from '../utils/date';

interface AddToDashboardModalProps {
  open: boolean;
  onClose: () => void;
  dashboards: Array<{
    dashboard_id: string;
    name: string;
    description?: string;
    created_at: string;
    /** 'manual' boards are built from a spec and take no widgets. */
    dashboard_type?: string | null;
  }>;
  loading: boolean;
  error: string | null;
  selectedDashboardId: string | null;
  setSelectedDashboardId: (v: string | null) => void;
  newDashboardName: string;
  setNewDashboardName: (v: string) => void;
  widgetName: string;
  setWidgetName: (v: string) => void;
  onSubmit: () => void;
  creating: boolean;
  submitError: string | null;
}

export default function AddToDashboardModal({
  open,
  onClose,
  dashboards,
  loading,
  error,
  selectedDashboardId,
  setSelectedDashboardId,
  newDashboardName,
  setNewDashboardName,
  widgetName,
  setWidgetName,
  onSubmit,
  creating,
  submitError,
}: AddToDashboardModalProps) {
  const [query, setQuery] = React.useState('');
  React.useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  // Only dashboards that take widgets are offered. A manual (spec-driven)
  // board is laid out from its spec — a chart added to it would never show —
  // so it is left out here, whichever screen opened the modal.
  const widgetDashboards = React.useMemo(
    () => dashboards.filter((d) => d.dashboard_type !== 'manual'),
    [dashboards],
  );
  const shown = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return widgetDashboards;
    return widgetDashboards.filter((d) => `${d.name} ${d.description ?? ''}`.toLowerCase().includes(q));
  }, [widgetDashboards, query]);

  // One or the other: picking a dashboard clears the new name, and typing a
  // new name clears the pick, so it's always clear where the widget goes.
  const creatingNew = newDashboardName.trim().length > 0;
  const canAdd = creatingNew || Boolean(selectedDashboardId);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add to dashboard"
      size="md"
      busy={creating}
      footer={
        <>
          <Button color="inherit" onClick={onClose} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={!canAdd || creating}
            startIcon={creating ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {creating ? 'Adding…' : creatingNew ? 'Create and add' : 'Add'}
          </Button>
        </>
      }
    >
      <Stack spacing={3}>
        <TextField label="Widget name" fullWidth value={widgetName} onChange={(e) => setWidgetName(e.target.value)} />

        <Box component="section" aria-labelledby="add-to-dashboard-choose">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 1.5 }}>
            <Typography variant="subtitle2" component="h3" id="add-to-dashboard-choose">
              Choose a dashboard
            </Typography>
            {widgetDashboards.length > 0 && (
              <Box sx={{ width: { xs: '100%', sm: 240 } }}>
                <SearchField value={query} onChange={setQuery} placeholder="Search dashboards…" />
              </Box>
            )}
          </Box>
          {error && (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {error}
            </Alert>
          )}
          {loading ? (
            <EmptyState message="Loading dashboards…" height={120} />
          ) : (
            <TilePicker
              ariaLabel="Dashboards"
              options={shown.map((d) => ({
                value: d.dashboard_id,
                title: d.name,
                description: d.description?.trim() || undefined,
                meta: `Created ${formatRelativeTime(d.created_at)}`,
                icon: <BarChartRounded fontSize="small" />,
              }))}
              value={creatingNew ? null : selectedDashboardId}
              onChange={(id) => {
                setSelectedDashboardId(id);
                setNewDashboardName('');
              }}
              maxHeight={300}
              emptyMessage={
                query.trim() ? `No dashboards match “${query.trim()}”.` : 'No dashboards yet. Create one below.'
              }
            />
          )}
        </Box>

        <Box component="section" aria-labelledby="add-to-dashboard-new">
          <Typography variant="subtitle2" component="h3" id="add-to-dashboard-new" sx={{ mb: 1.5 }}>
            Or create a new dashboard
          </Typography>
          <TextField
            label="New dashboard name"
            fullWidth
            value={newDashboardName}
            onChange={(e) => {
              setNewDashboardName(e.target.value);
              if (e.target.value.trim()) setSelectedDashboardId(null);
            }}
          />
        </Box>

        {submitError && <Alert severity="error">{submitError}</Alert>}
      </Stack>
    </Modal>
  );
}
